import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Attempt } from '../../types';
import {
  FileSpreadsheet,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  X,
  Trophy,
  Check,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../common/ConfirmModal';

export const AttemptsManager: React.FC = () => {
  const { attempts, questions, deleteAttempt, deleteAllAttempts } = useApp();
  const { showToast } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'revision' | 'test'>('all');
  const [inspectAttempt, setInspectAttempt] = useState<Attempt | null>(null);
  const [deleteAttemptTarget, setDeleteAttemptTarget] = useState<Attempt | null>(null);
  const [isDeleteAllOpen, setIsDeleteAllOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredAttempts = attempts.filter((att) => {
    if (filterMode !== 'all' && att.mode !== filterMode) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const userName = att.user_name?.toLowerCase() || '';
      const setName = att.question_set_name?.toLowerCase() || '';
      if (!userName.includes(q) && !setName.includes(q)) return false;
    }
    return true;
  });

  const handleDeleteSingleAttempt = async () => {
    if (!deleteAttemptTarget) return;
    setIsSubmitting(true);
    try {
      const res = await deleteAttempt(deleteAttemptTarget.id);
      if (res.success) {
        showToast(res.message || 'Submission deleted successfully', 'success', 'Submission Deleted');
        setDeleteAttemptTarget(null);
      } else {
        showToast(res.error || 'Failed to delete submission', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAllAttempts = async () => {
    setIsSubmitting(true);
    try {
      const res = await deleteAllAttempts();
      if (res.success) {
        showToast(res.message || 'All submissions deleted successfully', 'success', 'Submissions Cleared');
        setIsDeleteAllOpen(false);
      } else {
        showToast(res.error || 'Failed to delete all submissions', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 pb-24 md:pb-16">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#111111] tracking-tight">Student Exam Submissions</h1>
          <p className="text-xs sm:text-sm text-[#5F6368]">
            Review candidate exam scores, completion durations, and full question response sheets.
          </p>
        </div>

        {attempts.length > 0 && (
          <button
            onClick={() => setIsDeleteAllOpen(true)}
            className="px-3.5 py-2 text-xs font-bold rounded-xl bg-[#FDEDEF] hover:bg-[#D94A5C]/20 text-[#D94A5C] border border-[#D94A5C]/30 transition-all flex items-center gap-1.5 cursor-pointer self-start sm:self-auto"
          >
            <Trash2 className="w-4 h-4" />
            Delete All Submissions
          </button>
        )}
      </div>

      {/* Filter and Search */}
      <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)] flex flex-col sm:flex-row items-center gap-3">
        <div className="relative flex-1 w-full">
          <Search className="w-4 h-4 text-[#5F6368] absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search candidate name or question set..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-[#F7FAFA]"
          />
        </div>

        <div className="w-full sm:w-52">
          <select
            value={filterMode}
            onChange={(e) =>
              setFilterMode(e.target.value as 'all' | 'revision' | 'test')
            }
            className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white text-[#111111]"
          >
            <option value="all">All Practice Modes</option>
            <option value="test">Exam / Test Mode</option>
            <option value="revision">Revision Mode</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#E5E9E9] overflow-hidden shadow-[0_2px_12px_rgba(17,17,17,0.02)]">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#F7FAFA] text-[#5F6368] border-b border-[#E5E9E9]">
              <tr>
                <th className="py-3 px-4 font-bold uppercase tracking-wider text-[10px]">Student Candidate</th>
                <th className="py-3 px-4 font-bold uppercase tracking-wider text-[10px]">Question Set</th>
                <th className="py-3 px-4 font-bold uppercase tracking-wider text-[10px]">Mode</th>
                <th className="py-3 px-4 font-bold uppercase tracking-wider text-[10px]">Score</th>
                <th className="py-3 px-4 font-bold uppercase tracking-wider text-[10px]">Breakdown</th>
                <th className="py-3 px-4 font-bold uppercase tracking-wider text-[10px]">Duration</th>
                <th className="py-3 px-4 font-bold uppercase tracking-wider text-[10px]">Date Completed</th>
                <th className="py-3 px-4 font-bold uppercase tracking-wider text-[10px] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E9E9] text-[#111111]">
              {filteredAttempts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-[#5F6368] text-xs">
                    No student attempts found matching the current search criteria.
                  </td>
                </tr>
              ) : (
                filteredAttempts.map((att) => (
                  <tr key={att.id} className="hover:bg-[#E8FAF9]/30 transition-colors">
                    <td className="py-3.5 px-4 font-bold text-[#111111]">
                      {att.user_name || `Student #${att.user_id}`}
                    </td>
                    <td className="py-3.5 px-4 text-[#111111] font-medium">
                      {att.question_set_name || 'Practice Set'}
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full font-bold uppercase text-[10px] ${
                          att.mode === 'revision'
                            ? 'bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/40'
                            : 'bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/50'
                        }`}
                      >
                        {att.mode}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-black text-[#111111]">
                      <span
                        className={
                          att.score_percentage >= 70
                            ? 'text-[#20A66A]'
                            : att.score_percentage >= 50
                            ? 'text-[#8a6508]'
                            : 'text-[#D94A5C]'
                        }
                      >
                        {att.score_percentage}%
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="text-[#20A66A] font-bold">{att.correct_answers}C</span> /{' '}
                      <span className="text-[#D94A5C] font-bold">{att.wrong_answers}W</span> /{' '}
                      <span className="text-[#5F6368]">{att.skipped_answers}S</span>
                    </td>
                    <td className="py-3.5 px-4 text-[#5F6368] font-medium">
                      {Math.floor(att.time_spent_seconds / 60)}m {att.time_spent_seconds % 60}s
                    </td>
                    <td className="py-3.5 px-4 text-[#5F6368]">
                      {new Date(att.completed_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setInspectAttempt(att)}
                          className="px-2.5 py-1 text-xs font-bold rounded-lg bg-[#E8FAF9] hover:bg-[#56E8E1]/30 text-[#1b8e88] transition-colors inline-flex items-center gap-1 cursor-pointer"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Inspect
                        </button>

                        <button
                          onClick={() => setDeleteAttemptTarget(att)}
                          className="p-1.5 text-[#5F6368] hover:text-[#D94A5C] hover:bg-[#FDEDEF] rounded-lg border border-transparent hover:border-[#D94A5C]/20 transition-all cursor-pointer"
                          title="Delete Submission"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Inspect Attempt Modal */}
      {inspectAttempt && (
        <div className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-7 shadow-2xl border border-[#E5E9E9] max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-[#E5E9E9] mb-4 shrink-0">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#EBC138]" />
                <h3 className="text-base font-bold text-[#111111]">
                  Attempt Sheet: {inspectAttempt.user_name || `User #${inspectAttempt.user_id}`}
                </h3>
              </div>
              <button
                onClick={() => setInspectAttempt(null)}
                className="text-[#5F6368] hover:text-[#111111] p-1 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 rounded-2xl bg-[#F7FAFA] border border-[#E5E9E9] mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center shrink-0">
              <div>
                <div className="text-xs text-[#5F6368] font-bold uppercase">Score</div>
                <div className="text-xl font-black text-[#1b8e88]">
                  {inspectAttempt.score_percentage}%
                </div>
              </div>
              <div>
                <div className="text-xs text-[#5F6368] font-bold uppercase">Correct</div>
                <div className="text-xl font-black text-[#20A66A]">
                  {inspectAttempt.correct_answers}
                </div>
              </div>
              <div>
                <div className="text-xs text-[#5F6368] font-bold uppercase">Wrong</div>
                <div className="text-xl font-black text-[#D94A5C]">
                  {inspectAttempt.wrong_answers}
                </div>
              </div>
              <div>
                <div className="text-xs text-[#5F6368] font-bold uppercase">Time</div>
                <div className="text-xl font-black text-[#111111]">
                  {Math.floor(inspectAttempt.time_spent_seconds / 60)}m
                </div>
              </div>
            </div>

            {/* Answer List */}
            <div className="overflow-y-auto space-y-3 flex-1 pr-1">
              {inspectAttempt.answers.map((ans, i) => {
                const qObj = questions.find((q) => q.id === ans.question_id);
                return (
                  <div
                    key={i}
                    className={`p-3.5 rounded-xl border text-xs ${
                      ans.is_correct
                        ? 'bg-[#EAF8F1] border-[#20A66A]/30'
                        : ans.selected_option === null
                        ? 'bg-[#F7FAFA] border-[#E5E9E9]'
                        : 'bg-[#FDEDEF] border-[#D94A5C]/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <span className="font-bold text-[#111111]">
                        {i + 1}. {qObj?.question_text || 'Question Statement'}
                      </span>
                      <span
                        className={`font-bold uppercase text-[10px] px-2 py-0.5 rounded ${
                          ans.is_correct
                            ? 'bg-[#20A66A] text-white'
                            : ans.selected_option === null
                            ? 'bg-[#E5E9E9] text-[#5F6368]'
                            : 'bg-[#D94A5C] text-white'
                        }`}
                      >
                        {ans.is_correct ? 'Correct' : ans.selected_option === null ? 'Skipped' : 'Wrong'}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-[11px] text-[#5F6368]">
                      <span>
                        Candidate: <strong className="text-[#111111]">{ans.selected_option ? `Option ${ans.selected_option}` : 'None'}</strong>
                      </span>
                      <span>
                        Answer Key: <strong className="text-[#20A66A]">Option {ans.correct_option}</strong>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pt-3 border-t border-[#E5E9E9] flex justify-end shrink-0 mt-3">
              <button
                onClick={() => setInspectAttempt(null)}
                className="px-4 py-2 text-xs font-bold text-[#5F6368] hover:bg-[#F0F4F4] rounded-xl transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Single Submission Modal */}
      <ConfirmModal
        isOpen={!!deleteAttemptTarget}
        title="Delete Submission?"
        message="This will permanently delete this exam/revision submission and its answer history. The student account will not be deleted. This action cannot be undone."
        confirmText="Delete Submission"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteSingleAttempt}
        onCancel={() => setDeleteAttemptTarget(null)}
      />

      {/* Delete All Submissions Modal */}
      <ConfirmModal
        isOpen={isDeleteAllOpen}
        title="Delete All Exam Submissions?"
        message="This will permanently delete all student exam/revision submissions, scores, and answer records. User accounts and question data will remain. This action cannot be undone."
        confirmText="Delete All Submissions"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteAllAttempts}
        onCancel={() => setIsDeleteAllOpen(false)}
      />
    </div>
  );
};
